'use client'
import Link from 'next/link'
import { HideOnErrorImg } from '@/components/ui/hide-on-error-img'
import { Twitter, Linkedin, Youtube, ShieldCheck } from 'lucide-react'
import { NewsletterForm } from '@/components/marketing/newsletter-form'
import { SeeWhatAiSaysWidgetClient } from '@/components/marketing/see-what-ai-says-widget-client'
import { LanguageSwitcher } from './language-switcher'
import { CookiePreferencesLink } from '@/components/consent/cookie-preferences-link'
import { useTranslation } from '@/lib/i18n/use-translation'

// Compact footer, five columns of 5–6 links max, no overlap with
// each other or with the bottom legal strip. Every link appears once.
// `titleKey` ties each column heading to the i18n dictionary so the
// footer actually re-localizes when the language switcher (which lives
// in this same footer) flips locale.

const groups = [
  {
    title: 'Product',
    titleKey: 'product' as const,
    items: [
      { label: 'Features', href: '/features' },
      { label: 'Widgets', href: '/widgets' },
      { label: 'Integrations', href: '/integrations' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Getting started', href: '/getting-started' },
      { label: 'Compare alternatives', href: '/compare' },
      { label: 'Digital signage', href: '/digital-signage' },
      { label: 'Demo store', href: '/demo-store' },
    ],
  },
  {
    title: 'Solutions',
    titleKey: 'solutions' as const,
    items: [
      { label: 'The Idukki Advantage', href: '/idukki-advantage' },
      { label: 'Industries', href: '/industries' },
      { label: 'Customers', href: '/customers' },
      { label: 'Case studies', href: '/case-studies' },
      { label: 'Showcase', href: '/showcase' },
      { label: 'Enterprise', href: '/enterprise' },
      { label: 'Security', href: '/security' },
    ],
  },
  {
    title: 'Free tools',
    titleKey: 'tools' as const,
    items: [
      { label: 'Theme detector', href: '/tools/shopify-theme-detector' },
      { label: 'UGC ROI calculator', href: '/tools/ugc-roi-calculator' },
      { label: 'A/B significance', href: '/tools/ab-significance' },
      { label: 'Switching savings calc.', href: '/tools/savings-calculator' },
      { label: 'All tools', href: '/tools' },
    ],
  },
  {
    title: 'Resources',
    titleKey: 'resources' as const,
    items: [
      { label: 'Blog', href: '/blog' },
      { label: 'UGC: complete guide', href: '/resources/ugc-complete-guide' },
      { label: 'UGC statistics', href: '/resources/ugc-statistics' },
      { label: 'State of UGC 2026', href: '/resources/state-of-ugc-2026' },
      { label: 'Glossary', href: '/glossary' },
      { label: 'Guides + ebooks', href: '/resources' },
    ],
  },
  {
    title: 'Developers',
    titleKey: 'developers' as const,
    items: [
      { label: 'Developer docs', href: '/developers' },
      { label: 'API platform', href: '/api-platform' },
      { label: 'Help centre', href: '/docs' },
      { label: 'Platforms', href: '/platforms' },
      { label: 'Product sync', href: '/product-sync' },
      { label: 'Open source', href: '/open-source' },
    ],
  },
  {
    title: 'Company',
    titleKey: 'company' as const,
    items: [
      { label: 'Team', href: '/team' },
      { label: 'Careers', href: '/careers' },
      { label: 'Press', href: '/press' },
      { label: 'Contact', href: '/contact' },
      { label: 'What’s new', href: '/whats-new' },
    ],
  },
]

export function SiteFooter() {
  const { t } = useTranslation()
  return (
    <footer className="paper-grain mt-24 border-t bg-secondary/30">
      {/* Newsletter band, full-width across the top of the footer */}
      <div className="border-b bg-background/40">
        <div className="container-wide py-8 md:py-10">
          <div className="grid items-center gap-6 md:grid-cols-[1fr_minmax(0,420px)] md:gap-10">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary">
                {t.footer.newsletter}
              </p>
              <h3 className="mt-1.5 text-balance font-display text-xl font-medium tracking-tight md:text-2xl">
                {t.footer.nextPost}
              </h3>
              <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">{t.footer.monthly}</p>
            </div>
            <NewsletterForm source="footer" variant="pill" />
          </div>
        </div>
      </div>

      <div className="container-wide py-10 md:py-12">
        <div className="grid gap-10 lg:grid-cols-7">
          <div className="lg:col-span-1">
            <Link href="/" className="font-display text-xl font-medium tracking-tight">
              Idukki
            </Link>
            <p className="mt-3 max-w-xs text-xs text-muted-foreground">
              The full-stack UGC platform for modern commerce.
            </p>
            {/* Official Shopify App Store badge, used unaltered per Shopify's
                brand guidelines: no recolour/border/box/shadow, height >= 30px,
                clear space preserved, linked to the app listing. */}
            <a
              href="https://apps.shopify.com/idukki-no-code-ugc-platform"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 block w-fit"
              aria-label="Idukki on the Shopify App Store"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/brand/shopify-app-store-badge.svg"
                alt="Available on the Shopify App Store"
                width={180}
                height={40}
                loading="lazy"
                className="h-10 w-auto"
              />
            </a>
            {/* Official Product Hunt embed badges (unaltered, served from PH's
                own API by post_id=400380). #5 Product of the Day and #4
                Product of the Week, July 6th 2023 launch. */}
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href="https://www.producthunt.com/products/idukki/launches/idukki-1?embed=true&utm_source=badge-top-post-badge&utm_medium=badge&utm_campaign=badge-idukki-1"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Idukki, #5 Product of the Day on Product Hunt"
              >
                <HideOnErrorImg
                  src="https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=400380&theme=light&period=daily"
                  alt="Idukki - Collect, moderate & display UGC across marketing channels | Product Hunt"
                  width={185}
                  height={40}
                  loading="lazy"
                  className="h-10 w-auto"
                />
              </a>
              <a
                href="https://www.producthunt.com/products/idukki/launches/idukki-1?embed=true&utm_source=badge-top-post-topic-badge&utm_medium=badge&utm_campaign=badge-idukki-1"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Idukki, #4 Product of the Week on Product Hunt"
              >
                <HideOnErrorImg
                  src="https://api.producthunt.com/widgets/embed-image/v1/top-post-topic-badge.svg?post_id=400380&theme=light&period=weekly&topic_id=164"
                  alt="Idukki - Collect, moderate & display UGC across marketing channels | Product Hunt"
                  width={185}
                  height={40}
                  loading="lazy"
                  className="h-10 w-auto"
                />
              </a>
            </div>
            {/* GDPR compliance badge (self-attested). Links to the GDPR page. */}
            <Link
              href="/gdpr"
              className="mt-3 flex w-fit items-center gap-1.5 rounded-full border bg-background/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-primary" /> GDPR compliant
            </Link>
            <div className="mt-4 flex gap-3 text-muted-foreground">
              <a
                href="https://twitter.com/idukki_io"
                aria-label="Idukki on Twitter"
                className="hover:text-foreground"
              >
                <Twitter className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Idukki on Twitter</span>
              </a>
              <a
                href="https://www.linkedin.com/company/idukki"
                aria-label="Idukki on LinkedIn"
                className="hover:text-foreground"
              >
                <Linkedin className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Idukki on LinkedIn</span>
              </a>
              <a
                href="https://www.youtube.com/@idukki"
                aria-label="Idukki on YouTube"
                className="hover:text-foreground"
              >
                <Youtube className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Idukki on YouTube</span>
              </a>
            </div>
            <a
              href="https://www.upekkha.io/up-portfolio/idukki"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-[11px] text-muted-foreground transition hover:text-foreground"
            >
              <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary">
                Backed by
              </span>
              <HideOnErrorImg
                src="https://cdn.prod.website-files.com/6660300a285c80dfaaad807f/6661afde449afc09a5b070ef_64f1751d6e8978948f7e1e2e_imresizer-1693545466814.png"
                alt="Upekkha"
                loading="lazy"
                className="h-5 w-auto"
                fallback={<span className="text-xs font-semibold text-foreground">Upekkha</span>}
              />
            </a>
          </div>
          {groups.map((g) => (
            <div key={g.title}>
              <div className="text-xs font-semibold uppercase tracking-wider">
                {t.footer[g.titleKey]}
              </div>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {g.items.map((i) => {
                  const isExternal = /^https?:/.test(i.href)
                  return (
                    <li key={i.href + i.label}>
                      {isExternal ? (
                        <a
                          href={i.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-foreground"
                        >
                          {i.label}
                        </a>
                      ) : (
                        <Link href={i.href} className="hover:text-foreground">
                          {i.label}
                        </Link>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 w-1/2 min-w-[280px]">
          <SeeWhatAiSaysWidgetClient />
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <span>&copy; {new Date().getFullYear()} Idukki</span>
            <LanguageSwitcher />
          </div>
          <div className="flex flex-wrap gap-4">
            <Link href="/privacy-policy" className="hover:text-foreground">
              Privacy
            </Link>
            <Link href="/terms" className="hover:text-foreground">
              Terms
            </Link>
            <Link href="/gdpr" className="hover:text-foreground">
              GDPR
            </Link>
            <CookiePreferencesLink />
            <Link href="/site-index" className="hover:text-foreground">
              Site index
            </Link>
            <Link href="/status" className="hover:text-foreground">
              Status
            </Link>
            <a href="mailto:hello@idukki.io" className="hover:text-foreground">
              hello@idukki.io
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
